import sleep from '@/utils/sleep';
import {
  AbortMultipartUpload,
  CompleteMultipartUpload,
  GetMoreMultipartEndpoints,
  GetMultipartEndpoints,
  KeepAliveMultipartUpload,
  MULTIPART_PARTS_PER_REQUEST,
} from './api';
import type {
  MultipartResumeRecord,
  MultipartUploadedPart,
  MultipartUploadOptions,
  MultipartUploadResult,
} from './types';
import { deleteResumableUpload, resumeIdFor, saveResumableUpload } from './resume-store';
import {
  ExpiredSignatureError,
  MultipartUploadExpiredError,
  MultipartUploadInterruptedError,
  NetworkInterruptedError,
} from './errors';
import {
  KEEP_ALIVE_INTERVAL_MS,
  MAX_CONCURRENT_PARTS,
  MAX_PART_ATTEMPTS,
  MAX_STALLED_MS,
  MULTIPART_PART_SIZE_BYTES,
  MULTIPART_THRESHOLD_BYTES,
  NETWORK_RETRY_INTERVAL_MS,
  NETWORK_SETTLE_MS,
  PART_STALL_TIMEOUT_MS,
  RESPONSE_STALL_TIMEOUT_MS,
  URL_REFRESH_AFTER_MS,
} from './constants';

export function shouldUseMultipartUpload(file: File | Blob): boolean {
  return file.size > MULTIPART_THRESHOLD_BYTES;
}

/**
 * True for the failures that mean "the network went away" rather than "S3 said no".
 *
 * The signing calls go through fetch rather than XHR, so they surface a plain TypeError instead of
 * a NetworkInterruptedError - being offline at the point we catch is the best signal we get.
 */
function isNetworkFailure(e: unknown): boolean {
  return e instanceof NetworkInterruptedError || !navigator.onLine;
}

/**
 * Resolves once there is some prospect of the network working again, or after
 * NETWORK_RETRY_INTERVAL_MS regardless.
 *
 * Waiting on the `online` event alone isn't enough: it doesn't fire on every suspend/resume path,
 * and navigator.onLine can be true the whole time while a sleeping machine's requests are being
 * killed anyway. So this is a poll with an early wake up, and the caller retries on a loop.
 */
function waitForNetwork(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;

      window.removeEventListener('online', finish);
      signal?.removeEventListener('abort', finish);
      window.clearTimeout(poll);

      // give a just-restored interface a beat before handing it another 100MB
      window.setTimeout(resolve, NETWORK_SETTLE_MS);
    };

    const poll = window.setTimeout(finish, NETWORK_RETRY_INTERVAL_MS);

    if (signal?.aborted) return finish();

    window.addEventListener('online', finish);
    signal?.addEventListener('abort', finish);
  });
}

function uploadPart(args: {
  url: string;
  body: Blob;
  partNumber: number;
  onProgress: (loadedBytes: number) => void;
  registerRequest: (request: XMLHttpRequest) => void;
}): Promise<string> {
  const { url, body, partNumber, onProgress, registerRequest } = args;

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    // A suspended network doesn't always error the request - the socket can be left open and
    // silent - so treat the request going quiet as a failure of its own, otherwise nothing ever
    // rejects and the part sits there looking active forever.
    //
    // Two phases with very different timings: while the body is going out we expect a steady stream
    // of progress events, and once it's all sent we're just waiting on S3 to answer.
    let stallTimer: number | undefined;
    let stalled = false;

    const stopStallTimer = () => window.clearTimeout(stallTimer);

    const armStallTimer = (timeout: number) => {
      stopStallTimer();
      stallTimer = window.setTimeout(() => {
        stalled = true;
        // aborting is what surfaces the stall - onabort turns it back into an error below
        request.abort();
      }, timeout);
    };

    registerRequest(request);
    request.open('PUT', url);

    request.upload.onprogress = (e: ProgressEvent<EventTarget>) => {
      armStallTimer(PART_STALL_TIMEOUT_MS);
      onProgress(Math.min(e.loaded, body.size));
    };

    // body fully sent, so progress events stop here - switch to waiting on the response
    request.upload.onloadend = () => armStallTimer(RESPONSE_STALL_TIMEOUT_MS);

    // status is 0 for anything that failed below HTTP: offline, ERR_NETWORK_IO_SUSPENDED from a
    // sleeping machine, DNS, TLS. All of them are worth waiting out rather than failing the upload.
    request.onerror = () => {
      stopStallTimer();
      reject(new NetworkInterruptedError(`Lost the connection uploading part ${partNumber}`));
    };

    request.ontimeout = () => {
      stopStallTimer();
      reject(new NetworkInterruptedError(`Part ${partNumber} timed out`));
    };

    request.onabort = () => {
      stopStallTimer();

      if (stalled) {
        return reject(new NetworkInterruptedError(`Part ${partNumber} stopped making progress`));
      }

      reject(new Error(`Upload of part ${partNumber} was cancelled`));
    };

    request.onload = () => {
      stopStallTimer();

      // S3 rejects an out of date signature with a 403, which we can recover from by signing again
      if (request.status === 403) {
        return reject(new ExpiredSignatureError(`Signature for part ${partNumber} has expired`));
      }

      if (request.status < 200 || request.status >= 300) {
        return reject(new Error(`Part ${partNumber} failed with status ${request.status}`));
      }

      // S3 returns each part's checksum in the ETag header and needs the full set back to
      // assemble the object. Reading it cross origin requires ETag in the bucket's CORS
      // ExposeHeaders list.
      const etag = request.getResponseHeader('ETag');
      if (!etag) {
        return reject(new Error(`Part ${partNumber} did not return an ETag, check the bucket's CORS ExposeHeaders`));
      }

      resolve(etag);
    };

    // armed before send so a connection that dies without ever reporting progress is caught too
    armStallTimer(PART_STALL_TIMEOUT_MS);

    // slicing without a content type keeps XHR from adding a header the signature doesn't cover
    request.send(body);
  });
}

/**
 * Whether a saved record actually belongs to this file and this upload. The part size check is the
 * important one: S3 needs every part but the last to be the same size, so resuming a file that was
 * cut differently would assemble a corrupt object rather than fail.
 */
function canResume(record: MultipartResumeRecord, channelId: number, file: File, contentType: string): boolean {
  return (
    record.channelId === channelId &&
    record.fileSize === file.size &&
    record.fileLastModified === file.lastModified &&
    record.contentType === contentType &&
    record.partSize === MULTIPART_PART_SIZE_BYTES &&
    record.uploadedParts.length > 0
  );
}

/**
 * Splits a large file into 100MB segments and uploads them as an S3 multipart upload.
 *
 * Part urls are signed 10 at a time, so anything over 1GB requests more urls from the API as it
 * works through the file. If any part fails past its retries the whole upload is aborted server
 * side so we don't leave partial objects sitting in the bucket.
 *
 * Progress is written to IndexedDB as each part lands, so an upload the network killed can be
 * resumed by passing the record from findResumableUpload back in as `options.resume` - the parts
 * already in S3 are skipped instead of re-sent.
 */
export async function UploadFileMultipart(options: MultipartUploadOptions): Promise<MultipartUploadResult> {
  const {
    channelId,
    file,
    filename,
    contentType = file.type,
    onProgress,
    onNetworkInterruption,
    headers,
    signal,
    resume,
  } = options;

  const partCount = Math.max(1, Math.ceil(file.size / MULTIPART_PART_SIZE_BYTES));

  const signedUrls = new Map<number, string>();
  const batchSignedAt = new Map<number, number>();

  const batchOf = (partNumber: number) => Math.floor((partNumber - 1) / MULTIPART_PARTS_PER_REQUEST);

  const recordBatch = (batch: number, presignedUrls: Record<number, string>) => {
    const offset = batch * MULTIPART_PARTS_PER_REQUEST;

    // the API keys its response 1..10 within the batch, so shift them onto real part numbers
    for (const [positionInBatch, url] of Object.entries(presignedUrls)) {
      signedUrls.set(Number(positionInBatch) + offset, url);
    }

    batchSignedAt.set(batch, Date.now());
  };

  // A record that doesn't match the file is ignored rather than fatal - the file was re-picked and
  // turned out to be a different one, so the only sensible thing left is a fresh upload.
  let resumeFrom = resume && canResume(resume, channelId, file, contentType) ? resume : undefined;

  if (resumeFrom) {
    // The record says what we managed to upload, not whether the API still has the upload - it
    // reaps them after an hour of inactivity, and a presigned url is just a signature so it would
    // be handed out for a dead upload id quite happily. Ask first, so a reaped upload starts over
    // cleanly instead of failing every part against something that no longer exists.
    let alive: boolean;

    try {
      alive = await KeepAliveMultipartUpload(channelId, resumeFrom.key, resumeFrom.uploadId);
    } catch (e) {
      // only a 410 is the API telling us it's gone - anything else is inconclusive, and a blip
      // shouldn't cost us the resume
      console.error(e);
      alive = true;
    }

    if (!alive) {
      await deleteResumableUpload(resumeFrom.id);
      resumeFrom = undefined;
    }
  }

  const resumeId = resumeFrom?.id ?? resumeIdFor(channelId, file);

  let key: string;
  let uploadId: string;
  let cdnHost: string | undefined;
  let objectOrigin: string;

  if (resumeFrom) {
    ({ key, uploadId, cdnHost, objectOrigin } = resumeFrom);
  } else {
    const firstBatch = await GetMultipartEndpoints(channelId, filename, partCount, contentType, headers);
    ({ key, uploadId, cdnHost } = firstBatch);

    recordBatch(0, firstBatch.presignedUrls);

    // the multipart response has no bucket url of its own, so fall back to the signed url's origin,
    // which is what the single request upload path resolves to when there is no CDN host
    const firstSignedUrl = signedUrls.get(1);
    const origin = firstBatch.cdnHost ?? (firstSignedUrl ? new URL(firstSignedUrl).origin : undefined);

    if (!origin) {
      throw new Error('Multipart upload endpoint returned no signed urls');
    }

    objectOrigin = origin;
  }

  const signingInFlight = new Map<number, Promise<void>>();

  const signBatch = (batch: number): Promise<void> => {
    const pending = signingInFlight.get(batch);
    if (pending) return pending;

    const offset = batch * MULTIPART_PARTS_PER_REQUEST;
    const remaining = partCount - offset;

    const request = GetMoreMultipartEndpoints(
      channelId,
      filename,
      Math.min(MULTIPART_PARTS_PER_REQUEST, remaining),
      uploadId,
      key,
      offset,
      contentType,
      headers
    )
      .then((batchResponse) => recordBatch(batch, batchResponse.presignedUrls))
      .finally(() => signingInFlight.delete(batch));

    signingInFlight.set(batch, request);
    return request;
  };

  const urlForPart = async (partNumber: number): Promise<string> => {
    const batch = batchOf(partNumber);
    const signedAt = batchSignedAt.get(batch);

    if (!signedUrls.has(partNumber) || !signedAt || Date.now() - signedAt > URL_REFRESH_AFTER_MS) {
      await signBatch(batch);
    }

    const url = signedUrls.get(partNumber);
    if (!url) {
      throw new Error(`The API did not return a signed url for part ${partNumber}`);
    }

    return url;
  };

  const sizeOfPart = (partNumber: number) => {
    const start = (partNumber - 1) * MULTIPART_PART_SIZE_BYTES;
    return Math.min(start + MULTIPART_PART_SIZE_BYTES, file.size) - start;
  };

  // index 0 is unused so the array lines up with S3's 1 based part numbers
  const uploadedPerPart = Array.from({ length: partCount + 1 }, () => 0);
  const uploadedParts: MultipartUploadedPart[] = resumeFrom ? [...resumeFrom.uploadedParts] : [];
  const activeRequests = new Set<XMLHttpRequest>();

  // parts already in S3 from an earlier run - skipped below, and counted as progress up front so
  // the bar picks up where it left off instead of starting from zero
  const alreadyUploaded = new Set(uploadedParts.map((part) => part.PartNumber));

  alreadyUploaded.forEach((partNumber) => {
    uploadedPerPart[partNumber] = sizeOfPart(partNumber);
  });

  let nextPart = 1;
  let failure: unknown;

  /** When a part last finished, i.e. how long the upload has been getting nowhere. */
  let lastCompletedAt = Date.now();
  let interrupted = false;

  const reportProgress = () => {
    onProgress?.(uploadedPerPart.reduce((total, loaded) => total + loaded, 0));
  };

  // a resumed upload already has parts accounted for, so publish that before anything is sent -
  // otherwise the bar sits at zero until the first part of this run reports in
  if (alreadyUploaded.size) reportProgress();

  /**
   * Only fires on a change, so the caller can show "waiting for network" instead of a progress bar
   * that has apparently frozen for no reason.
   */
  const reportInterruption = (isInterrupted: boolean) => {
    if (isInterrupted === interrupted) return;
    interrupted = isInterrupted;
    onNetworkInterruption?.(isInterrupted);
  };

  const abortInFlight = () => {
    activeRequests.forEach((request) => request.abort());
    activeRequests.clear();
  };

  /**
   * Snapshots what has landed so far. Called after every part rather than only on failure, because
   * the failures worth surviving - a killed tab, a crash - don't give us a chance to run anything.
   * Deliberately not awaited by callers: a storage write failing is not a reason to fail an upload.
   *
   * Workers race here, so the last write wins and can be a part or two behind. That costs a re-sent
   * part on resume, nothing worse - a part number can be PUT again and simply replaces itself.
   */
  const saveProgress = () =>
    saveResumableUpload({
      id: resumeId,
      channelId,
      key,
      uploadId,
      filename,
      contentType,
      partSize: MULTIPART_PART_SIZE_BYTES,
      fileName: file.name,
      fileSize: file.size,
      fileLastModified: file.lastModified,
      objectOrigin,
      cdnHost,
      uploadedParts: [...uploadedParts],
      createdAt: resumeFrom?.createdAt ?? Date.now(),
      updatedAt: Date.now(),
    });

  const pingKeepAlive = async () => {
    try {
      const alive = await KeepAliveMultipartUpload(channelId, key, uploadId);
      if (!alive && !failure) {
        failure = new MultipartUploadExpiredError();
        abortInFlight();
      }
    } catch (e) {
      // a failed keep alive on its own isn't fatal, the next one may succeed
      console.error(e);
    }
  };

  const keepAlive = window.setInterval(pingKeepAlive, KEEP_ALIVE_INTERVAL_MS);

  // a suspended machine misses interval ticks entirely, so ping as soon as the connection is back
  // rather than waiting up to KEEP_ALIVE_INTERVAL_MS to refresh the API's inactivity timer - and to
  // find out straight away if the upload was already dropped while we were away
  const onReconnect = () => void pingKeepAlive();
  window.addEventListener('online', onReconnect);

  /**
   * Uploads a single segment, retrying transient failures. Throws once the attempts run out.
   *
   * Network interruptions are handled separately and deliberately do NOT spend an attempt. A
   * sleeping machine kills every part in flight at once, and the attempts would otherwise all be
   * burned in the first few seconds after waking - before wifi has even reassociated - failing an
   * upload that only needed to wait. Those are bounded by how long the upload as a whole has gone
   * without completing anything (lastCompletedAt) instead of by a count.
   */
  const uploadPartWithRetries = async (partNumber: number): Promise<void> => {
    const start = (partNumber - 1) * MULTIPART_PART_SIZE_BYTES;
    const body = file.slice(start, Math.min(start + MULTIPART_PART_SIZE_BYTES, file.size));

    let attempt = 0;

    while (attempt < MAX_PART_ATTEMPTS) {
      let request: XMLHttpRequest | undefined;

      try {
        const url = await urlForPart(partNumber);

        const etag = await uploadPart({
          url,
          body,
          partNumber,
          onProgress: (loaded) => {
            uploadedPerPart[partNumber] = loaded;
            reportProgress();
          },
          registerRequest: (xhr) => {
            request = xhr;
            activeRequests.add(xhr);
          },
        });

        uploadedPerPart[partNumber] = body.size;
        reportProgress();
        uploadedParts.push({ PartNumber: partNumber, ETag: etag });

        lastCompletedAt = Date.now();
        reportInterruption(false);
        void saveProgress();
        return;
      } catch (e) {
        // don't count the abandoned bytes towards progress on the retry
        uploadedPerPart[partNumber] = 0;
        reportProgress();

        if (failure || signal?.aborted) throw e;

        if (isNetworkFailure(e)) {
          // measured from the last part to finish anywhere in the upload, so a connection that
          // keeps flapping without ever completing a part still eventually gives up
          if (Date.now() - lastCompletedAt > MAX_STALLED_MS) {
            throw e instanceof NetworkInterruptedError ? e : new NetworkInterruptedError(String(e));
          }

          reportInterruption(true);

          // we have no idea how long we were cut off, so don't trust the existing signature.
          // re-signing also counts as activity on the API's inactivity timer, and tells us early if
          // the upload has already been dropped server side.
          batchSignedAt.delete(batchOf(partNumber));

          await waitForNetwork(signal);

          // waitForNetwork returns on cancellation too, so don't start another attempt in that case
          if (failure || signal?.aborted) throw e;

          continue;
        }

        attempt++;
        if (attempt >= MAX_PART_ATTEMPTS) throw e;

        if (e instanceof ExpiredSignatureError) {
          // force a fresh signature for the whole batch before trying again
          batchSignedAt.delete(batchOf(partNumber));
        } else {
          await sleep(attempt * 1000);
        }
      } finally {
        if (request) activeRequests.delete(request);
      }
    }
  };

  /** One worker in the pool - claims the next unstarted segment until the file runs out. */
  const uploadNextParts = async (): Promise<void> => {
    while (nextPart <= partCount) {
      if (failure) return;

      if (signal?.aborted) {
        failure ??= new Error('Upload was cancelled');
        return;
      }

      // claimed synchronously so two workers never take the same segment
      const partNumber = nextPart++;

      // already in S3 from the run this is resuming
      if (alreadyUploaded.has(partNumber)) continue;

      try {
        await uploadPartWithRetries(partNumber);
      } catch (e) {
        failure ??= e;
        return;
      }
    }
  };

  try {
    const workers = Array.from({ length: Math.min(MAX_CONCURRENT_PARTS, partCount) }, () => uploadNextParts());

    await Promise.all(workers);

    if (failure) throw failure;

    // S3 wants the parts listed in order
    uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);

    const complete = await CompleteMultipartUpload(channelId, key, uploadId, uploadedParts);
    if (!complete) {
      throw new Error('Unable to assemble the uploaded segments');
    }

    await deleteResumableUpload(resumeId);

    return { url: `${objectOrigin}/${key}`, key, cdnHost };
  } catch (e) {
    abortInFlight();

    // An upload we lost the connection to is still assembleable - every part already in S3 stays
    // valid and the API's inactivity timer decides when it's really gone - so leave it and its
    // resume record alone rather than throwing away however many GB made it up.
    if (e instanceof NetworkInterruptedError) {
      throw new MultipartUploadInterruptedError(e);
    }

    // Anything else is a real failure: drop the server side upload so we don't leave a partial
    // object behind, and the record with it so nothing offers to resume something that's gone.
    // Neither is allowed to mask the original error.
    await deleteResumableUpload(resumeId);

    if (!(e instanceof MultipartUploadExpiredError)) {
      await AbortMultipartUpload(channelId, key, uploadId);
    }

    throw e;
  } finally {
    window.clearInterval(keepAlive);
    window.removeEventListener('online', onReconnect);
  }
}
